import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useUpload } from '@/hooks/useUpload';
import { useDisplayName, ANONYMOUS_NAME } from '@/hooks/useDisplayName';
import { formatBytes } from '@/lib/compression';
import { Toast } from '@/components/ui/Toast';

interface UploadFormProps {
  eventName: string;
}

interface SelectedFile {
  id: string;
  file: File;
  preview: string;
}

type Status = 'idle' | 'uploading' | 'success' | 'error';

export default function UploadForm({ eventName }: UploadFormProps) {
  const { name: savedName, setName: saveName } = useDisplayName();
  const [name, setName] = useState(''); // input curent (poate fi editat)
  const [editingName, setEditingName] = useState(false); // toggle input vizibil
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [currentStep, setCurrentStep] = useState<{ current: number; total: number } | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const { uploadBatch, progress, isUploading, reset } = useUpload();

  // Clean up preview object URLs on unmount
  useEffect(() => {
    return () => {
      selectedFiles.forEach((f) => URL.revokeObjectURL(f.preview));
    };
  }, [selectedFiles]);

  const addFiles = (incoming: FileList | File[]) => {
    const valid: SelectedFile[] = [];
    let tooBig = false;

    Array.from(incoming).forEach((f) => {
      if (!f.type.startsWith('image/')) return;
      if (f.size > 15 * 1024 * 1024) {
        tooBig = true;
        return;
      }
      valid.push({
        id: `${f.name}-${f.lastModified}-${Math.random()}`,
        file: f,
        preview: URL.createObjectURL(f),
      });
    });

    if (tooBig) {
      setToast({ message: 'Unele imagini depășesc 15MB și au fost omise', type: 'error' });
    }

    if (valid.length > 0) {
      setSelectedFiles((prev) => [...prev, ...valid]);
    }
  };

  const removeFile = (id: string) => {
    setSelectedFiles((prev) => {
      const target = prev.find((f) => f.id === id);
      if (target) URL.revokeObjectURL(target.preview);
      return prev.filter((f) => f.id !== id);
    });
  };

  const clearAllFiles = () => {
    selectedFiles.forEach((f) => URL.revokeObjectURL(f.preview));
    setSelectedFiles([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedFiles.length === 0) return;

    // Nume final: dacă se editează sau s-a introdus un nume nou în input, acela are prioritate absolută!
    const inputName = name.trim();
    const finalName = ((editingName && inputName) ? inputName : (inputName || savedName || ANONYMOUS_NAME)).trim() || ANONYMOUS_NAME;

    // Guard: numele este OBLIGATORIU la upload
    if (!finalName || finalName === ANONYMOUS_NAME) {
      setToast({ message: 'Te rugăm să introduci un nume înainte de a încărca poze.', type: 'error' });
      return;
    }
    setStatus('uploading');
    setCurrentStep({ current: 0, total: selectedFiles.length });

    try {
      if (finalName && finalName !== savedName && finalName !== ANONYMOUS_NAME) {
        await saveName(finalName);
        setEditingName(false);
      }

      const blobs = selectedFiles.map((f) => f.file);
      const res = await uploadBatch(blobs, finalName, (completed, total) => {
        setCurrentStep({ current: completed, total });
      });

      if (res.successful > 0) {
        setStatus('success');
        setToast({
          message:
            res.failed === 0
              ? `Mulțumim! ${res.successful === 1 ? 'Poza a fost trimisă' : `Toate cele ${res.successful} poze au fost trimise`} spre aprobare.`
              : `${res.successful} poze trimise, ${res.failed} au eșuat.`,
          type: res.failed === 0 ? 'success' : 'error',
        });
      } else {
        setStatus('error');
        // eslint-disable-next-line no-console
        console.log('[UploadForm] errors array:', res.errors, 'first item type:', typeof res.errors[0]);
        setToast({ message: String(res.errors[0] ?? 'A apărut o eroare la încărcare'), type: 'error' });
      }
      setTimeout(() => {
        clearAllFiles();
        setStatus('idle');
        setCurrentStep(null);
        reset();
      }, 3500);
    } catch (err) {
      setStatus('error');
      const detail = errorToString(err);
      setToast({ message: `Eroare: ${detail}`, type: 'error' });
      console.error('[UploadForm] failed:', err);
    }
  };

  const showNameInput = !savedName || editingName;
  const isValid = selectedFiles.length > 0 && status !== 'success' && !isUploading;
  const totalBytes = selectedFiles.reduce((acc, f) => acc + f.file.size, 0);

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className="rounded-lg border border-border bg-bg-alt/80 p-lg"
        style={{ boxShadow: 'var(--shadow-md)' }}
      >
        <h2 className="text-center font-serif text-2xl">Trimite fotografii</h2>
        <p className="mt-sm text-center text-sm text-fg-soft">{eventName}</p>

        <div className="mt-lg space-y-lg">
          {/* Name input */}
          {showNameInput ? (
            <div className="input-floating">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder=" "
                maxLength={50}
                disabled={isUploading || status === 'success'}
                className="w-full border-0 border-b-2 border-border bg-transparent px-0 py-sm outline-none focus:border-accent"
              />
              <label>Numele tău (opțional — îl păstrăm pentru pozele viitoare)</label>
            </div>
          ) : (
            <div className="flex items-center justify-between border-b border-border pb-sm">
              <p className="text-sm">
                <span className="text-fg-soft">Trimitem ca </span>
                <span className="font-serif italic text-fg">{savedName || ANONYMOUS_NAME}</span>
              </p>
              <button
                type="button"
                onClick={() => {
                  setName(savedName);
                  setEditingName(true);
                }}
                disabled={isUploading || status === 'success'}
                className="text-xs text-fg-soft underline-offset-2 hover:text-fg hover:underline disabled:opacity-50"
              >
                Schimbă numele
              </button>
            </div>
          )}

          {/* Hidden Inputs: 1 for direct camera, 1 for gallery multiple */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={(e) => e.target.files && addFiles(e.target.files)}
            className="hidden"
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => e.target.files && addFiles(e.target.files)}
            className="hidden"
          />

          {/* Upload buttons: Camera directă + Galerie multiplă */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-sm">
            <button
              type="button"
              onClick={() => cameraInputRef.current?.click()}
              disabled={isUploading}
              className="btn-secondary flex items-center justify-center gap-2 py-3 border-accent/40 hover:bg-accent/10 transition-colors"
            >
              <span className="text-lg">📸</span>
              <span className="font-medium text-sm">Fă o poză acum</span>
            </button>

            <button
              type="button"
              onClick={() => galleryInputRef.current?.click()}
              disabled={isUploading}
              className="btn-secondary flex items-center justify-center gap-2 py-3 hover:bg-accent/10 transition-colors"
            >
              <span className="text-lg">🖼️</span>
              <span className="font-medium text-sm">Alege din galerie</span>
            </button>
          </div>

          {/* Preview grid */}
          {selectedFiles.length > 0 && (
            <div className="space-y-sm">
              <div className="flex items-center justify-between text-xs text-fg-soft px-1">
                <span>
                  {selectedFiles.length}{' '}
                  {selectedFiles.length === 1 ? 'poză selectată' : 'poze selectate'} ({formatBytes(totalBytes)})
                </span>
                <button
                  type="button"
                  onClick={clearAllFiles}
                  disabled={isUploading}
                  className="text-fg-soft hover:text-error underline"
                >
                  Șterge tot
                </button>
              </div>

              <div className="grid grid-cols-3 gap-xs max-h-64 overflow-y-auto p-1 rounded-md border border-border bg-bg-alt">
                {selectedFiles.map((sf) => (
                  <div key={sf.id} className="relative aspect-square rounded overflow-hidden border border-border bg-bg group">
                    <img
                      src={sf.preview}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    {!isUploading && (
                      <button
                        type="button"
                        onClick={() => removeFile(sf.id)}
                        className="absolute top-1 right-1 bg-black/70 hover:bg-error text-white rounded-full w-5 h-5 flex items-center justify-center text-xs transition-colors"
                        title="Elimină poza"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Progress bar */}
          <AnimatePresence>
            {isUploading && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-xs"
              >
                <div className="h-2 overflow-hidden rounded-full bg-border">
                  <motion.div
                    className="h-full bg-accent"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.2 }}
                  />
                </div>
                <div className="flex justify-between text-xs text-fg-soft font-mono">
                  <span>
                    {currentStep
                      ? `Se încarcă ${currentStep.current} din ${currentStep.total}...`
                      : 'Se trimite...'}
                  </span>
                  <span>{progress}%</span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Submit */}
          <button
            type="submit"
            disabled={!isValid}
            className="btn-primary w-full py-3 text-base font-medium shadow-sm transition-all"
          >
            {status === 'success'
              ? '✓ Trimis cu succes!'
              : isUploading
              ? `Se încarcă (${progress}%)...`
              : selectedFiles.length > 1
              ? `Trimite ${selectedFiles.length} poze`
              : selectedFiles.length === 1
              ? 'Trimite poza'
              : 'Selectează sau fă o poză'}
          </button>

          <p className="text-center text-xs text-fg-soft">
            Pozele tale vor apărea în galerie imediat după aprobare.
          </p>
        </div>
      </form>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </>
  );
}

/**
 * Converteste orice valoare la un string util pentru toast/UI.
 * - Error instance -> message
 * - Plain object cu .message (PostgrestError, etc.) -> message
 * - Plain object -> JSON
 * - Altfel -> String(value)
 */
function errorToString(err: unknown): string {
  if (err == null) return 'Eroare necunoscută';
  if (err instanceof Error) return err.message;
  if (typeof err === 'object') {
    const obj = err as Record<string, unknown>;
    if (typeof obj.message === 'string') return obj.message;
    try {
      return JSON.stringify(err);
    } catch {
      return 'Eroare (obiect nereprezentabil)';
    }
  }
  return String(err);
}
